import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
	AdvancedSearchDTO,
	AdvancedSearchResponseDTO,
	ChargesFilter,
	ItemPopover,
	ItemPopoverService,
	Offset,
	PageDTO,
	PopoverResponseMap,
	SubscriberFilter,
	SubscriberFilterSearch,
	SubscriberResponse
} from '@interfaces'
import { BehaviorSubject, map, Observable, of } from 'rxjs'
import { ChargeResponse } from '../interfaces/Charge.interface'
import {
	canExecuteRemoteTextSearch,
	normalizeRemoteSearchStringParam,
	normalizeRemoteTextSearchTerm
} from './remote-text-search.helper'
type Visibility = 'ONLY_BAR' | 'ONLY_NON_BAR' | 'ALL'
type GlobalSearchClearMode = 'visual' | 'full'

const EMAIL_LIKE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NUMERIC_PATTERN = /^\d+$/

@Injectable({
	providedIn: 'root'
})
export class FilterService implements ItemPopoverService {
	private http = inject(HttpClient)

	// Observable para guardar los datos seleccionados en el filtro
	private _advancedSearchSubject = new BehaviorSubject<AdvancedSearchDTO>({})
	advancedSearch$ = this._advancedSearchSubject.asObservable()

	// Observable para conocer los documentos seleccionados
	private _advancedSearchResponseSubjet = new BehaviorSubject<AdvancedSearchResponseDTO>({})
	advancedSearchResponse$ = this._advancedSearchResponseSubjet.asObservable()

	// Observable que controla la cadena de texto que se introduce en el buscador para realizar la búsqueda simple
	searchString: BehaviorSubject<string> = new BehaviorSubject<string>('')
	searchString$ = this.searchString.asObservable()
	_searchString: string

	private cookiesSubject = new BehaviorSubject<Set<string>>(new Set())
	cookiesSubject$ = this.cookiesSubject.asObservable()

	// Observable que controla la anchura del filtro en función de las diferencias de distancias (offsets) respecto a los laterales
	offsets: BehaviorSubject<Offset> = new BehaviorSubject<Offset>({})
	offsets$ = this.offsets.asObservable()
	_offsets: Offset

	// Observable para cambiar el filtro a abierto o cerrado
	filterOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
	filterOpen$ = this.filterOpen.asObservable()

	url = '/opportunities/interactionsType'

	getFilteredSubscriber(page = 1, size = 15, active = true, filter: SubscriberFilter, visibility: Visibility = 'ALL') {
		const pageIdx = page
		return this.http.post<SubscriberResponse>(`/subscribers/filter`, filter, {
			params: { page: pageIdx, size, active, visibility }
		})
	}
	getFilteredSubscriberBar(page = 1, size = 15, active = true, filter: SubscriberFilter) {
		const pageIdx = page
		return this.http.post<SubscriberResponse>(`/subscribers/bar/filter`, filter, {
			params: { page: pageIdx, size, active }
		})
	}

	getFilteredCharger(page: number = 0, size: number = 15, filter: ChargesFilter) {
		const url = `/filter/searchCharges?page=${page}&size=${size}`
		return this.http.post<ChargeResponse>(url, filter)
	}

	getSubscriberSearchBar(page = 1, size = 15, active = true, filter: SubscriberFilter, visibility: Visibility = 'ALL') {
		const pageIdx = page
		const normalizedFilter = this.mapSubscriberTextFilter(filter)
		let params = new HttpParams().set('page', String(pageIdx)).set('size', String(size)).set('active', String(active))

		if (visibility === 'ONLY_BAR') {
			params = params.set('scope', 'bar')
		}

		return this.http.post<SubscriberResponse>(`/subscribers/filter/text`, normalizedFilter, {
			params
		})
	}

	// Abre o cierra el filtro modificando el observable
	openCloseFilter(value: boolean) {
		this.filterOpen.next(value)
	}

	// Resetea el string del simple search
	resetSearchString() {
		this._searchString = ''
		this.searchString.next(this._searchString)
	}

	clearGlobalSearch(mode: GlobalSearchClearMode = 'full') {
		this.resetSearchString()
		this.filterOpen.next(false)

		if (mode === 'visual') return

		this._advancedSearchSubject.next({})
		this._advancedSearchResponseSubjet.next({})
		localStorage.removeItem('advancedSearch')
	}

	// Resetea la búsqueda avanzada
	resetAdvancedSearch() {
		this._advancedSearchSubject.next({})
		this._advancedSearchResponseSubjet.next({})
		localStorage.removeItem('advancedSearch')
	}

	// Resetea los offsets del filtro para ajustar su ancho adecuadamente
	resetOffsets() {
		this._offsets = {}
		this.offsets.next(this._offsets)
	}

	// Settea el string del simple search
	setSearchString(search: string) {
		this._searchString = search
		this.searchString.next(this._searchString)
	}

	// Settea los offsets del filtro para ajustar su ancho adecuadamente
	setOffsets(values: Offset) {
		this._offsets = values
		this.offsets.next(this._offsets)
	}

	setCookiesFilter(newValue: Set<string>) {
		this.cookiesSubject.next(newValue)
	}

	normalizeSubscriberTextSearchTerm(search: string | null | undefined): string {
		return normalizeRemoteTextSearchTerm(search)
	}

	isValidSubscriberTextSearch(search: string | null | undefined): boolean {
		const normalizedSearch = this.normalizeSubscriberTextSearchTerm(search)
		return canExecuteRemoteTextSearch(normalizedSearch, {
			allowShortSearch: term => this.isEmailLikeSearchTerm(term) || this.isNumericSearchTerm(term)
		})
	}

	// Realiza una búsqueda simple (por texto)
	simpleSearch(search: string): Observable<SubscriberFilterSearch[]> {
		const normalizedSearch = normalizeRemoteSearchStringParam(search, {
			allowShortSearch: term => this.isEmailLikeSearchTerm(term) || this.isNumericSearchTerm(term)
		})

		if (!normalizedSearch) {
			return of([])
		}

		return this.http.get<SubscriberFilterSearch[]>(`/filter/simpleSearch?search=${normalizedSearch}`)
	}

	getPayMethods(): Observable<PageDTO<any>> {
		const URL = `/pay-method?page=1&size=15`
		return this.http.get<PageDTO<any>>(URL)
	}

	// Setea en el observable los documentos seleccionados en los checkbox
	setAdvancedSearch(advancedSearchDTO: AdvancedSearchDTO) {
		this._advancedSearchSubject.next(advancedSearchDTO)
	}

	// Setea los valores seleccionados para filtrar
	setAdvancedSearchResponse(advancedSearchResponseDTO: AdvancedSearchResponseDTO) {
		this._advancedSearchResponseSubjet.next(advancedSearchResponseDTO)
	}

	private mapSubscriberTextFilter(filter: SubscriberFilter): SubscriberFilter {
		const searchTerm = this.normalizeSubscriberTextSearchTerm(filter?.searchString ?? filter?.matchingSearch)

		if (!searchTerm) {
			return {
				...filter,
				searchString: undefined,
				matchingSearch: undefined
			}
		}

		return {
			...filter,
			searchString: searchTerm,
			matchingSearch: undefined
		}
	}

	private isEmailLikeSearchTerm(search: string): boolean {
		return EMAIL_LIKE_PATTERN.test(search)
	}

	private isNumericSearchTerm(search: string): boolean {
		return NUMERIC_PATTERN.test(search)
	}

	getItemPopoverData(_page: number, _size: number): Observable<PopoverResponseMap> {
		return this.getPayMethods().pipe(
			map(response => ({
				...response,
				content: response.content.map(
					({ id, srReference }): ItemPopover => ({
						id: id,
						value: srReference,
						active: false
					})
				)
			}))
		)
	}
}
